/* eslint-disable import/order */
import { enableAkitaProdMode } from '@datorama/akita';
import { initAnalytics } from 'analytics';
import { warnDevErrors, whyDidYouRender } from './utils/devtools';

const loadDevTools = async () => {
   const { akitaDevtools } = await import('@datorama/akita');
   akitaDevtools({
      sortAlphabetically: true,
   });
};

if (process.env.NODE_ENV === 'production') {
   enableAkitaProdMode();
} else {
   if (process.env.REACT_APP_AKITA_DEVTOOLS !== 'false') {
      loadDevTools().catch((error) => console.error('Could not load the Akita devtools:', error));
   }

   console.log('ENV:', process.env.NODE_ENV, '|', 'REACT_APP_DOMAIN:', process.env.REACT_APP_DOMAIN);

   if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_WYDR === 'true') {
      whyDidYouRender();
   }

   if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_WARN_DEV_ERRORS === 'true') {
      console.log('(!) REACT_APP_WARN_DEV_ERRORS:', process.env.REACT_APP_WARN_DEV_ERRORS);
      warnDevErrors();
   }
}

import { library } from '@fortawesome/fontawesome-svg-core';
import { all } from '@awesome.me/kit-54facd2604/icons';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import App from 'components/App/App';
import { UserContextComponent } from 'context/UserContext';
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'react-grid-layout/css/styles.css';
import { HelmetProvider } from 'react-helmet-async';
import 'react-resizable/css/styles.css';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import ApplicationService from 'state/Application/service';
import 'styles/globals.scss';
import { getSecrets } from 'utils/secrets';
import packageInfo from '../package.json';
import { codes } from 'currency-codes';
import { createUnit } from 'mathjs';

createUnit(
   {
      kgCO2e: {
         prefixes: 'long',
      },
      gCO2e: { definition: '0.001 kgCO2e' },
      tCO2e: { definition: '1000 kgCO2e' },
      lbCO2e: { definition: '0.45359237 kgCO2e' },
      MtCO2e: { definition: '1000000000 kgCO2e' },
      GtCO2e: { definition: '1000000000000 kgCO2e' },
   },
   {
      override: true,
   }
);
codes().forEach((currencyCode) => createUnit(currencyCode));

const applicationService = new ApplicationService();

LicenseInfo.setLicenseKey(
   'e64718ebce8db3911b26082e3ca9863eTz0xMjYzNjIsRT0xNzk2MDgzMTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTYsQVQ9bXVsdGksS1Y9Mg=='
);

void (async () => {
   let hasLoadedRecaptcha = false;
   let hasLoadedMaps = false;

   try {
      const data = await getSecrets();
      const { googleMapsClientAPIKey: apiKey, 'recaptcha-siteKey': sitekey, mapBoxAccessToken, surveyJsLicenseKey, NODE_ENV, CUSTOMER } = data;

      void initAnalytics({ customer: CUSTOMER, runtimeEnv: NODE_ENV, projectKey: data.posthogKey });

      if (NODE_ENV) {
         applicationService.setProperty('NODE_ENV', NODE_ENV);
      }

      if (CUSTOMER) {
         applicationService.setProperty('CUSTOMER', CUSTOMER);
      }

      if (surveyJsLicenseKey) {
         applicationService.setProperty('surveyJsLicenseKey', surveyJsLicenseKey);
      }

      if (sitekey) {
         applicationService.setProperty('siteKey', sitekey);
         const script = document.createElement('script');
         script.type = 'text/javascript';
         script.async = true;
         script.src = `https://www.google.com/recaptcha/enterprise.js?render=${sitekey}`;
         script.onload = () => {
            hasLoadedRecaptcha = true;

            if (hasLoadedMaps) {
               applicationService.setLoading(false);
            }
         };
         script.onerror = () => {
            console.debug('Failed to load reCAPTCHA script.');
            hasLoadedRecaptcha = true;

            if (hasLoadedMaps) {
               applicationService.setLoading(false);
            }
         };
         document.head.appendChild(script);
      } else {
         applicationService.setLoading(false);
      }

      if (apiKey) {
         applicationService.setProperty('googleMapsAPIKey', apiKey);

         setOptions({
            key: apiKey,
            v: 'weekly',
         });

         try {
            await importLibrary('places');
            hasLoadedMaps = true;
         } catch (error) {
            applicationService.setError(error as Error);
         } finally {
            if (hasLoadedRecaptcha) {
               applicationService.setLoading(false);
            }
         }
      }

      if (mapBoxAccessToken) {
         applicationService.setProperty('mapBoxAccessToken', mapBoxAccessToken);
      }
   } catch (error) {
      applicationService.setError(error as Error);
   }
})();

if (['production'].includes(process.env.NODE_ENV ?? '') && !process.env.REACT_APP_DOMAIN) {
   const environment =
      process.env.NODE_ENV === 'production' && /demo\.envoria\.app/i.test(window.location.hostname) ? 'development' : process.env.NODE_ENV;

   Sentry.init({
      dsn: 'https://e19b55a824f0bf56c478a7c0f5557c28@o4507479916347392.ingest.de.sentry.io/4507480733974608',
      enabled: environment !== 'development',
      release: packageInfo.version,
      integrations: [
         Sentry.replayIntegration(),
         Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
         }),
         Sentry.consoleLoggingIntegration({ levels: ['error'] }),
      ],
      tracePropagationTargets: [/^https:\/\/\w*\.demo\.envoria\.app/, /^https:\/\/\w*\.envoria\.app/, /^https:\/\/\w*\.haniel\.de/],
      // Performance Monitoring
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0, //  Capture 100% of the transactions
      // Session Replay
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      dataCollection: {
         userInfo: false,
         cookies: false,
         httpHeaders: {
            request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
         },
         httpBodies: [],
         urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
         genAI: { inputs: false, outputs: false },
         databaseQueryData: false,
         queues: false,
         graphQL: { document: false, variables: false },
      },
   });
}

library.add(...all);

const container = document.getElementById('app');

const root = createRoot(container!);

root.render(
   <React.StrictMode>
      <HelmetProvider>
         <UserContextComponent>
            <App />
         </UserContextComponent>
      </HelmetProvider>
   </React.StrictMode>
);
